import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import LoaderSvgComponent from 'webapp/components/loader-svg';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type Forecast from 'webapp/models-custom/forecast';

export enum ForecastHeaderAction {
	SHARE = 'share',
	RECALCULATE = 'recalculate',
	DUPLICATE = 'duplicate',
	DELETE = 'delete',
}

interface MoreActionOption {
	action: ForecastHeaderAction;
	text: string;
	icon: IconName;
	class?: string;
	isDisabled?: boolean;
}

interface Args {
	forecast: Forecast | null;
	originalForecast: Forecast | null;
	isForecastSaved: boolean;
	isRecalculateDisabled?: boolean;
	isLoading: boolean;
	isError: boolean;
	onSave: () => void;
	onRecalculate: () => void;
	onDuplicate: () => void;
	onDelete: () => void;
	onShare?: () => void;
	onClose?: () => void;
}

export default class ForecastsHeaderActionsComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;

	// to fix linting error - will always be non-null in that case
	get nonNullableOriginalForecast(): Forecast {
		return this.args.originalForecast!;
	}

	get hasError(): boolean {
		return this.args.isError;
	}

	get canShowSaveButton(): boolean {
		return !this.args.isForecastSaved && !this.args.isLoading && !this.hasError;
	}

	get hasAnyRegressorError(): boolean {
		const forecast = this.args.forecast;
		if (!forecast?.regressors || forecast.regressors.length === 0) {
			return false;
		}
		return forecast.regressors.some((regressor) => regressor.hasStatusError);
	}

	get isSaveButtonDisabled(): boolean {
		const forecast = this.args.forecast;
		if (!forecast) {
			return true;
		}
		// Disable if forecast has error or any regressor has error
		return forecast.hasStatusError || this.hasAnyRegressorError;
	}

	get saveButtonTooltip(): string | undefined {
		if (this.isSaveButtonDisabled) {
			return "Forecast can't be saved because there are errors on impacting metrics";
		}
		return undefined;
	}

	get moreActionsOptions(): MoreActionOption[] {
		const options: MoreActionOption[] = [];

		if (this.args.onShare) {
			options.push({
				action: ForecastHeaderAction.SHARE,
				text: 'Copy URL',
				icon: 'Link',
			});
		}

		options.push(
			{
				action: ForecastHeaderAction.RECALCULATE,
				text: 'Recalculate with latest data',
				icon: 'Refresh',
				isDisabled: this.args.isRecalculateDisabled,
			},
			{
				action: ForecastHeaderAction.DUPLICATE,
				text: 'Duplicate',
				icon: 'Copy',
			},
			{
				action: ForecastHeaderAction.DELETE,
				text: 'Delete',
				icon: 'Trash',
				class: 'delete',
			},
		);

		return options;
	}

	@action
	addToMyForecasts(): void {
		if (this.isSaveButtonDisabled) {
			return;
		}
		this.args.onSave();
	}

	@action
	openMoreActions(event: Event): void {
		const element = event.target as HTMLElement;
		const popup = this.popupService.createListPopup(element, '', '');

		if (popup) {
			popup.open({
				itemsList: this.moreActionsOptions,
				doAction: (actionName: string) => {
					void popup.close();
					this.handleAction(actionName as ForecastHeaderAction);
				},
			});
		}
	}

	private handleAction(action: ForecastHeaderAction): void {
		switch (action) {
			case ForecastHeaderAction.SHARE:
				this.args.onShare?.();
				break;
			case ForecastHeaderAction.RECALCULATE:
				this.args.onRecalculate();
				break;
			case ForecastHeaderAction.DUPLICATE:
				this.args.onDuplicate();
				break;
			case ForecastHeaderAction.DELETE:
				this.args.onDelete();
				break;
		}
	}
	<template>
		{{#if @forecast}}
			{{#if @isLoading}}
				<div class="updating-forecast-status">
					<span class="updating-spinner"><LoaderSvgComponent /></span>
					<span class="updating-text">Updating data...</span>
				</div>
			{{else if @isForecastSaved}}
				<div class="saved-forecast-info">
					<CommonUiIconComponent @name="Forecasts" @size="md" />
					<EditableModelPropertyComponent
						@model={{this.nonNullableOriginalForecast}}
						@canEdit={{true}}
						@editableProperty="title"
						@className="forecast-title"
					/>
					<div role="button" class="more-button" {{on "click" this.openMoreActions}}>
						<CommonUiIconComponent @name="Ellipsis" @size="md" />
					</div>
				</div>
			{{else if this.canShowSaveButton}}
				{{#if @onShare}}
					<SubmitButtonComponent
						@text="Copy URL"
						@icon="Link"
						@theme="grey-outlined"
						@size="normal"
						@onTrigger={{@onShare}}
						@noDelay={{true}}
					/>
				{{/if}}
				<SubmitButtonComponent
					@text="Save forecast"
					@icon="Plus"
					@theme="blue"
					@size="normal"
					@onTrigger={{this.addToMyForecasts}}
					@isDisabled={{this.isSaveButtonDisabled}}
					@tooltipText={{this.saveButtonTooltip}}
					@noDelay={{true}}
				/>
			{{/if}}
		{{/if}}

		{{#if @onClose}}
			<a role="button" class="metric-details__close" {{on "click" @onClose}}>
				<CommonUiIconComponent @name="Close" @size="lg" />
			</a>
		{{/if}}
	</template>
}
